import React, { useState, useContext, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { NavLink } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { GlobalContext } from "../../../../context/GlobalState";
import { ReactComponent as Loader } from "../../../LearningResources/Assement/Questions/Loader/Loader.svg";
import { LockOutlined } from "@material-ui/icons";

// -----------------------------
// ✅ Styles
// -----------------------------
const useStyles = makeStyles((theme) => ({
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: "fit-content",
  },
  tableHeading: {
    "& > th": {
      fontWeight: "bolder",
      fontSize: "1.02em",
    },
  },
  tableRow: {
    textDecoration: "none",
    "& > td": {
      fontWeight: "bold",
    },
    "&:hover": {
      backgroundColor: "#f0f0f0 !important",
    },
  },
}));

// -----------------------------
// ✅ Helper Functions
// -----------------------------

const widen = (val) => ({ minWidth: `${val}px` });

const convertArrayToString = (array = []) =>
  array.length ? array.map((item) => item.name).join(", ") : "";

const msToTime = (duration) => {
  if (duration <= 0) return "--:--:--";
  const seconds = Math.floor((duration / 1000) % 60);
  const minutes = Math.floor((duration / (1000 * 60)) % 60);
  const hours = Math.floor((duration / (1000 * 60 * 60)) % 24);
  return `${hours}hr ${minutes}min ${seconds}sec`;
};

const getStatus = (test) => {
  const submissionStatus = test?.submissions?.[0]?.submissionStatus;
  if (submissionStatus === "submitted") return "In Review";
  if (submissionStatus === "done") return "Done";
  if(submissionStatus === "checked") return "View Report";
  const now = new Date().toISOString();
  if (!test.submissions?.length && test.to < now) return "Not Attempted";
  if (test.to > now && test.from < now || submissionStatus === "Test In Progress") return "In Progress";
  if (test.from > now) return "Assigned";
  return "-";
};

// -----------------------------
// ✅ Main Component
// -----------------------------
export default function TestTable({ rows = [], loading = false }) {
  const classes = useStyles();
  const isSmall = useMediaQuery({ query: "(max-width: 472px)" });
  const { userDetails: cls } = useContext(GlobalContext);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedRows = useMemo(
    () => rows.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [rows, page, rowsPerPage]
  );

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // -----------------------------
  // ✅ Render
  // -----------------------------
  if (loading) return <Loader />;

  return (
    <div className={classes.tableContainer}>
      <TableContainer component={Paper}>
        <Table className={classes.table} aria-label="test table">
          {/* ---------- Table Head ---------- */}
          <TableHead>
            <TableRow className={classes.tableHeading}>
              {[
                "No",
                "Test Title",
                "Class",
                "Subject",
                "Chapter",
                "Topic",
                "Start Date",
                "Duration",
                "End Date",
                "Type",
                "Total Marks",
                "Marks Obtained",
                "Status",
              ].map((heading) => (
                <TableCell key={heading} align="center">
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ---------- Table Body ---------- */}
          <TableBody>
            {paginatedRows.length ? (
              paginatedRows.map((row, index) => {
                const status = getStatus(row);
                const canOpen = ![
                  "Done",
                  "Assigned",
                  "In Review",
                  "Not Attempted",
                ].includes(status);
                let testLink = "#";
                if(canOpen) {
                  testLink = status === "View Report" ? `/myassessment/online-test/tests/${row._id}/report?testName=${row?.questionSet?.title}`:`/myassessment/online-test/tests/${row._id}`;
                }

                return (
                  <TableRow
                    key={row._id || index}
                    component={NavLink}
                    to={testLink}
                    className={classes.tableRow}
                    style={{
                      backgroundColor:
                        index % 2 ? "rgba(180, 107, 130, 0.15)" : "white",
                    }}
                  >
                    <TableCell align="center">{index + 1}</TableCell>
                    <TableCell align="center" style={widen(200)}>
                      {row.questionSet?.title}
                    </TableCell>
                    <TableCell align="center" style={widen(100)}>
                      {cls ? `${cls.grade}-${cls.section}` : "-"}
                    </TableCell>
                    <TableCell align="center" style={widen(200)}>
                      {row.subjectId?.name || "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{ minWidth: 220, wordBreak: "break-word" }}
                      dangerouslySetInnerHTML={{
                        __html: convertArrayToString(row.questionSet?.chapters),
                      }}
                    />
                    <TableCell
                      align="center"
                      style={{ minWidth: 220, wordBreak: "break-word" }}
                      dangerouslySetInnerHTML={{
                        __html: convertArrayToString(row.questionSet?.topics),
                      }}
                    />
                    <TableCell align="center" style={widen(150)}>
                      {row.from?.split("T")[0] || "-"}
                    </TableCell>
                    <TableCell align="center" style={widen(150)}>
                      {msToTime(row.duration)}
                    </TableCell>
                    <TableCell align="center" style={widen(150)}>
                      {row.to?.split("T")[0] || "-"}
                    </TableCell>
                    <TableCell align="center">{row.testType || "-"}</TableCell>
                    <TableCell align="center">
                      {row.questionSet?.totalMarks || "-"}
                    </TableCell>
                    <TableCell align="center">
                      {["Done","View Report"].includes(status)
                        ? row.submissions?.[0]?.totalScore
                        : "-"}
                    </TableCell>
                    <TableCell
                      align="center"
                      style={{
                        ...widen(isSmall ? 150 : 200),
                        display: "flex",
                        alignItems: "center",
                        justifyContent:"center"
                      }}
                    >
                      {status} {!canOpen ? <LockOutlined fontSize="0.8rem" /> : null}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No tests available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* ---------- Pagination ---------- */}
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Rows per page:"
        />
      </TableContainer>
    </div>
  );
}
